import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';
import { ko as koLocale } from 'date-fns/locale';
import { supabase } from '@/lib/supabase';
import { useShamanContext } from './useShamanContext';
import { formatKRW, formatDateKo, formatTimeKo } from '@/lib/format';
import { SLOT_MINUTES_OPTIONS } from '@mudong/shared';

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

interface AvailabilityRule {
  id: string;
  weekday: number;
  start_time: string;
  end_time: string;
  slot_minutes: number;
  price_krw: number;
  effective_from: string;
  effective_until: string | null;
}

interface AvailabilityException {
  id: string;
  date: string;
  kind: 'closed' | 'custom';
  start_time: string | null;
  end_time: string | null;
  slot_minutes: number | null;
  price_krw: number | null;
}

interface PreviewSlot {
  starts_at: string;
  duration_minutes: number;
  price_krw: number;
}

export function ShamanAvailabilityPage() {
  const { data: shamanCtx } = useShamanContext();
  const qc = useQueryClient();
  const shamanId = shamanCtx?.shaman.id;
  const slug = shamanCtx?.shaman.slug;

  const rulesQuery = useQuery({
    queryKey: ['availability-rules', shamanId],
    enabled: !!shamanId,
    queryFn: async (): Promise<AvailabilityRule[]> => {
      const { data, error } = await supabase
        .from('availability_rules')
        .select('id, weekday, start_time, end_time, slot_minutes, price_krw, effective_from, effective_until')
        .eq('shaman_id', shamanId!)
        .order('weekday')
        .order('start_time');
      if (error) throw error;
      return data ?? [];
    },
  });

  const exceptionsQuery = useQuery({
    queryKey: ['availability-exceptions', shamanId],
    enabled: !!shamanId,
    queryFn: async (): Promise<AvailabilityException[]> => {
      const today = new Date();
      const upcoming = new Date(today);
      upcoming.setDate(upcoming.getDate() + 60);
      const { data, error } = await supabase
        .from('availability_exceptions')
        .select('id, date, kind, start_time, end_time, slot_minutes, price_krw')
        .eq('shaman_id', shamanId!)
        .gte('date', today.toISOString().slice(0, 10))
        .lte('date', upcoming.toISOString().slice(0, 10))
        .order('date');
      if (error) throw error;
      return data ?? [];
    },
  });

  const previewQuery = useQuery({
    queryKey: ['availability-preview', slug],
    enabled: !!slug,
    queryFn: async (): Promise<PreviewSlot[]> => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const to = format(addDays(new Date(), 28), 'yyyy-MM-dd');
      const { data, error } = await supabase.rpc('get_available_slots', {
        p_slug: slug!,
        p_from_date: today,
        p_to_date: to,
      });
      if (error) throw error;
      return (data ?? []) as PreviewSlot[];
    },
  });

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['availability-rules'] });
    qc.invalidateQueries({ queryKey: ['availability-exceptions'] });
    qc.invalidateQueries({ queryKey: ['availability-preview'] });
  }

  const addRule = useMutation({
    mutationFn: async (rule: Omit<AvailabilityRule, 'id' | 'effective_from' | 'effective_until'>) => {
      const { error } = await supabase.from('availability_rules').insert({
        shaman_id: shamanId,
        ...rule,
      });
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  const deleteRule = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('availability_rules').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  const addClosed = useMutation({
    mutationFn: async (date: string) => {
      const { error } = await supabase.from('availability_exceptions').insert({
        shaman_id: shamanId,
        date,
        kind: 'closed',
      });
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  const deleteException = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('availability_exceptions').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  if (rulesQuery.isLoading) return <div className="text-ink-muted">불러오는 중…</div>;

  return (
    <div className="space-y-6">
      <RulesEditor
        rules={rulesQuery.data ?? []}
        onAdd={(r) => addRule.mutate(r)}
        onDelete={(id) => deleteRule.mutate(id)}
        addBusy={addRule.isPending}
      />

      <ExceptionsEditor
        exceptions={exceptionsQuery.data ?? []}
        onAdd={(d) => addClosed.mutate(d)}
        onDelete={(id) => deleteException.mutate(id)}
        addBusy={addClosed.isPending}
      />

      <PreviewCard slots={previewQuery.data ?? []} loading={previewQuery.isLoading} />
    </div>
  );
}

function RulesEditor(props: {
  rules: AvailabilityRule[];
  onAdd: (r: Omit<AvailabilityRule, 'id' | 'effective_from' | 'effective_until'>) => void;
  onDelete: (id: string) => void;
  addBusy: boolean;
}) {
  const [weekday, setWeekday] = useState<number>(1);
  const [start, setStart] = useState('14:00');
  const [end, setEnd] = useState('18:00');
  const [slotMin, setSlotMin] = useState<number>(60);
  const [price, setPrice] = useState<string>('50000');

  function submit() {
    if (start >= end) {
      alert('종료 시간은 시작보다 늦어야 합니다');
      return;
    }
    props.onAdd({
      weekday,
      start_time: start,
      end_time: end,
      slot_minutes: slotMin,
      price_krw: parseInt(price, 10) || 0,
    });
  }

  return (
    <section className="card space-y-4">
      <h2 className="text-xl font-bold">요일별 반복 시간</h2>
      <p className="text-base text-ink-muted">
        예약 가능한 시간대를 요일별로 추가해주세요. 같은 요일에 여러 구간을 만들 수 있어요.
      </p>

      {[0, 1, 2, 3, 4, 5, 6].map((wd) => {
        const dayRules = props.rules.filter((r) => r.weekday === wd);
        if (!dayRules.length) return null;
        return (
          <div key={wd} className="space-y-2">
            <div className="font-semibold">{WEEKDAY_LABELS[wd]}요일</div>
            <ul className="divide-y divide-ink-line">
              {dayRules.map((r) => (
                <li key={r.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="text-base">
                    {r.start_time.slice(0, 5)} ~ {r.end_time.slice(0, 5)} · {r.slot_minutes}분 · {formatKRW(r.price_krw)}
                  </div>
                  <button className="text-danger text-base" onClick={() => props.onDelete(r.id)}>삭제</button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <div className="rounded-lg bg-surface-alt p-4 space-y-3">
        <div className="font-semibold">새 구간 추가</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-base">
            <div className="label">요일</div>
            <select className="field" value={weekday} onChange={(e) => setWeekday(parseInt(e.target.value, 10))}>
              {WEEKDAY_LABELS.map((l, i) => (
                <option key={i} value={i}>{l}</option>
              ))}
            </select>
          </label>
          <label className="text-base">
            <div className="label">슬롯 길이</div>
            <select className="field" value={slotMin} onChange={(e) => setSlotMin(parseInt(e.target.value, 10))}>
              {SLOT_MINUTES_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}분</option>
              ))}
            </select>
          </label>
          <label className="text-base">
            <div className="label">시작</div>
            <input type="time" className="field" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className="text-base">
            <div className="label">종료</div>
            <input type="time" className="field" value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
          <label className="col-span-2 text-base">
            <div className="label">1회 가격 (원)</div>
            <input
              type="text"
              inputMode="numeric"
              className="field"
              value={price}
              onChange={(e) => setPrice(e.target.value.replace(/\D/g, ''))}
            />
          </label>
        </div>
        <button className="btn-primary w-full" onClick={submit} disabled={props.addBusy}>
          {props.addBusy ? '추가 중…' : '추가'}
        </button>
      </div>
    </section>
  );
}

function ExceptionsEditor(props: {
  exceptions: AvailabilityException[];
  onAdd: (date: string) => void;
  onDelete: (id: string) => void;
  addBusy: boolean;
}) {
  const [date, setDate] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));

  return (
    <section className="card space-y-3">
      <h2 className="text-xl font-bold">휴무 등록</h2>
      <p className="text-base text-ink-muted">
        반복 시간이 있는 날이라도 휴무로 지정하면 예약을 받지 않습니다.
      </p>
      {props.exceptions.length > 0 && (
        <ul className="divide-y divide-ink-line">
          {props.exceptions.map((e) => (
            <li key={e.id} className="py-2 flex items-center justify-between gap-3">
              <div className="text-base">
                {format(new Date(e.date), 'yyyy.MM.dd(EEE)', { locale: koLocale })} · {e.kind === 'closed' ? '휴무' : '커스텀'}
              </div>
              <button className="text-danger text-base" onClick={() => props.onDelete(e.id)}>해제</button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <label className="flex-1">
          <div className="label">날짜</div>
          <input type="date" className="field" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button
          className="btn-secondary"
          onClick={() => props.onAdd(date)}
          disabled={props.addBusy}
        >
          휴무 추가
        </button>
      </div>
    </section>
  );
}

function PreviewCard(props: { slots: PreviewSlot[]; loading: boolean }) {
  return (
    <section className="card space-y-3">
      <h2 className="text-xl font-bold">앞으로 4주 예약 가능 슬롯 미리보기</h2>
      {props.loading && <div className="text-ink-muted">계산 중…</div>}
      {!props.loading && !props.slots.length && (
        <p className="text-ink-muted">현재 예약 가능한 슬롯이 없어요. 반복 시간을 추가해주세요.</p>
      )}
      {props.slots.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {Object.entries(groupByDay(props.slots)).map(([day, slots]) => (
            <div key={day}>
              <div className="font-semibold">{formatDateKo(day)}</div>
              <div className="flex flex-wrap gap-2 mt-1">
                {slots.map((s) => (
                  <span key={s.starts_at} className="rounded bg-surface-alt px-2 py-1 text-base">
                    {formatTimeKo(s.starts_at, 'a h:mm')} · {formatKRW(s.price_krw)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function groupByDay(slots: PreviewSlot[]): Record<string, PreviewSlot[]> {
  const out: Record<string, PreviewSlot[]> = {};
  for (const s of slots) {
    const day = formatDateKo(s.starts_at, 'yyyy-MM-dd');
    if (!out[day]) out[day] = [];
    out[day].push(s);
  }
  return out;
}
